import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { Map } from 'immutable';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';

import { DcIcons } from '../../app-dc.icons';

type ConsumerElement = {
	label: string;
	elemUrl: string;
	type: string;
	subType: string
};

export type ConsumptionReport = {
	consumedWithin: Array<ConsumerElement>;
	consumedElementLabel: string;
};

export type DeleteReport = {
	code: number;
	msg: string;
	nonDeletedElementsCount: number;
	elementsDeletedCount: number;
	elementsDeleteRequestCount: number;
	elementsDependencies: Array<ConsumptionReport>;
	elementsUnauthorized: Array<string>;
};

export type ElementsSectionCollapseState = {
	unauthorizedOpened: boolean;
	dependenciesOpened: boolean;
};

type ViewConsumptionReport = ConsumptionReport & { isOpen: boolean };

@Component({
	selector: 'app-delete-report',
	templateUrl: './app-delete-report.component.html',
	styleUrls: ['./app-delete-report.component.scss'],
})
export class AppDeleteReportComponent<
	T extends DeleteReport
> extends DcBaseComponent {
	protected readonly DcIcons = DcIcons;

	public elementsSectionCollapse: ElementsSectionCollapseState = {
		unauthorizedOpened: false,
		dependenciesOpened: false,
	};

	public vo$: Observable<{
		message: string;
		totalNotDeleted: number;
		totalDeleted: number;
		totalRequestToDelete: number;
		elementsDependenciesReport: Map<string, ViewConsumptionReport>,
		elementsUnauthorized: Array<string>;
	}>;

	private readonly mappingSubject = new ReplaySubject<
		Map<string, ViewConsumptionReport>
	>(1);

	public constructor(
		private readonly modalService: AppModalService,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'delete-popup-report';

		const initialValue = Map<string, ViewConsumptionReport>(
			this.modalData.elementsDependencies.map((it) => [
				it.consumedElementLabel,
				{
					...it,
					isOpen: false,
				},
			])
		);

		this.mappingSubject.next(initialValue);

		this.vo$ = combineAll({
			message: this.modalData.msg,
			totalNotDeleted: this.modalData.nonDeletedElementsCount,
			totalDeleted: this.modalData.elementsDeletedCount,
			totalRequestToDelete: this.modalData.elementsDeleteRequestCount,
			elementsDependenciesReport: this.mappingSubject,
			elementsUnauthorized: this.modalData.elementsUnauthorized,
		});
	}

	public toggleSection(section: keyof ElementsSectionCollapseState): void {
		this.elementsSectionCollapse[section] = !this.elementsSectionCollapse[section];
	}

	public async toggleReportFor(consumedLabel: string): Promise<void> {
		const current = await firstValueFrom(this.mappingSubject);
		const val = current.get(consumedLabel);
		if (!val) {
			return;
		}
		val.isOpen = !val.isOpen;
		const updated = current.set(consumedLabel, val);
		this.mappingSubject.next(updated);
	}
}
